/* eslint-disable no-mixed-spaces-and-tabs */
import {
	Checkbox,
	FormControl,
	FormHelperText,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	TextFieldProps,
	Typography,
} from '@mui/material';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import { ReactNode, useState } from 'react';
import {
	FieldPath,
	FieldValues,
	UseControllerProps,
	useController,
} from 'react-hook-form';
import { Option } from '../../../api/DTO/common.interface';

// Sentinel value for the optional "add new" row. It is never written into the
// field value - clicking the row only closes the menu and fires the callback.
const ADD_NEW_VALUE = '__ADD_NEW__';

interface AppMultipleDropDownMenuProps {
	label?: string;
	options: Option[];
	infoPopUp?: ReactNode;
	emptyArrayIfNoSelection?: boolean;
	selectAllOptions?: {
		isSelectAll: boolean;
		text: string;
	};
	renderValue?: string;
	handleChange?: any;
	addNewOption?: {
		text: string;
		onClick: () => void;
		disabled?: boolean;
	};
	// Fields holding name snapshots can carry values that no longer match any
	// option (the record was renamed or removed). Set this to keep showing them
	// so they can at least be unselected, instead of silently hiding them.
	showUnmatchedValues?: boolean;
	unmatchedValueSuffix?: string;
}

export default function AppMultipleDropDownMenu<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
	label,
	options = [],
	infoPopUp,
	disabled,
	placeholder,
	required,
	emptyArrayIfNoSelection,
	selectAllOptions,
	renderValue,
	addNewOption,
	showUnmatchedValues,
	unmatchedValueSuffix,

	handleChange,
	...props
}: AppMultipleDropDownMenuProps &
	UseControllerProps<TFieldValues, TName> &
	TextFieldProps) {
	const { field, fieldState } = useController(props);
	const errorMessage = fieldState.error?.message;
	const [selectAll, setSelectAll] = useState(false);
	const [open, setOpen] = useState(false);

	const optionIds = new Set((options || []).map((option) => option.id));
	const unmatchedValues: any[] = showUnmatchedValues
		? (Array.isArray(field.value) ? field.value : []).filter(
				(value: any) =>
					value !== undefined && value !== null && !optionIds.has(value)
			)
		: [];
	const unmatchedLabel = (value: any) =>
		`${value}${unmatchedValueSuffix ? ` ${unmatchedValueSuffix}` : ''}`;

	function handleSelectAll(checkedName: any) {
		if (checkedName.length == 1 && checkedName?.includes(-1)) {
			setSelectAll(true);
			field.onChange(options.map((option) => option.id));
		} else if (checkedName.length > options.length) {
			setSelectAll(false);
			field.onChange([]);
		} else if (checkedName.length <= options.length && checkedName.length > 1) {
			setSelectAll(true);
			field.onChange(options.map((option) => option.id));
		} else if (!selectAll) {
			setSelectAll(true);
			field.onChange(options.map((option) => option.id));
		} else if (selectAll) {
			setSelectAll(false);
			field.onChange([]);
		}
	}
	function handleSelect(checkedName: any) {
		if (checkedName?.includes(-1)) {
			handleSelectAll(checkedName);
		} else if (checkedName.length == 0) {
			emptyArrayIfNoSelection ? field.onChange([]) : field.onChange(null);
		} else {
			field.onChange(checkedName);
		}
	}

	return (
		<FormControl
			error={!!fieldState.error?.message}
			fullWidth
			size={props.size}
		>
			<InputLabel>{label}</InputLabel>
			<Select
				{...field}
				label={label}
				multiple
				open={open}
				onOpen={() => setOpen(true)}
				onClose={() => setOpen(false)}
				onChange={(e) => {
					const value: any = e.target.value;
					// The "add new" row is an action, not a selectable value.
					if (Array.isArray(value) && value.includes(ADD_NEW_VALUE)) {
						setOpen(false);
						addNewOption?.onClick();
						return;
					}
					handleSelect(value);
					handleChange && handleChange(value);
				}}
				value={!field.value ? [] : field.value} // to fix `value` prop on `input` should not be null
				disabled={disabled}
				renderValue={(selected) => {
					if (selected.length >= options.length && renderValue)
						return renderValue;
					const optionsMap = (options || []).reduce(
						(map: Record<string, any>, option) => {
							map[option.id] = option.name;
							return map;
						},
						{}
					);

					// Ids with no matching option (stale/removed records) are dropped
					// rather than mapped to '', which would otherwise show up as a
					// stray separator such as ",name 1" - unless the field is asked to
					// keep showing them so they can be unselected.
					const arr = (selected || [])
						.map(
							(item: any) =>
								optionsMap[item] ??
								(showUnmatchedValues ? unmatchedLabel(item) : undefined)
						)
						.filter((name: any) => !!name);
					return arr.join(',');
				}}
				sx={{
					overflow: 'hidden',
					whiteSpace: 'nowrap',
					textOverflow: 'ellipsis',
				}}
			>
				{selectAllOptions?.isSelectAll && options.length > 0 && (
					<MenuItem key='selectAll' value={-1}>
						<Stack direction={'row'} alignItems={'center'}>
							<Checkbox
								checked={
									Array.isArray(field.value) &&
									field.value.length === options.length
								}
								indeterminate={
									!Array.isArray(field.value) ||
									(Array.isArray(field.value) &&
										field.value.length !== options.length &&
										field.value?.includes(-1))
								}
							/>
							<Typography variant='body1' color='black'>
								{selectAllOptions.text}
							</Typography>
						</Stack>
					</MenuItem>
				)}

				{(options || []).map((option) => (
					<MenuItem key={option.id} value={option.id}>
						<Stack direction={'row'} alignItems={'center'}>
							<Checkbox checked={field.value?.indexOf(option.id) > -1} />
							<Typography
								variant='body1'
								color={option.color ? option.color : 'black'}
							>
								{option.name}
							</Typography>
						</Stack>
					</MenuItem>
				))}

				{unmatchedValues.map((value) => (
					<MenuItem key={`unmatched-${value}`} value={value}>
						<Stack direction={'row'} alignItems={'center'}>
							<Checkbox checked />
							<Typography variant='body1' color='text.secondary'>
								{unmatchedLabel(value)}
							</Typography>
						</Stack>
					</MenuItem>
				))}

				{addNewOption && (
					<MenuItem
						key={ADD_NEW_VALUE}
						value={ADD_NEW_VALUE}
						disabled={addNewOption.disabled}
					>
						<Stack direction={'row'} alignItems={'center'} spacing={1}>
							<AddCircleOutlineOutlinedIcon
								fontSize='small'
								color={addNewOption.disabled ? 'disabled' : 'primary'}
							/>
							<Typography
								variant='body1'
								color={addNewOption.disabled ? 'text.disabled' : 'primary'}
							>
								{addNewOption.text}
							</Typography>
						</Stack>
					</MenuItem>
				)}
			</Select>
			<FormHelperText>{errorMessage}</FormHelperText>
		</FormControl>
	);
}
